import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Info } from 'lucide-react';
import { useIsDark } from '@/hooks/useTheme';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

// --- Types ---
type PlatformKey = 'booking-standard' | 'booking-preferred' | 'airbnb' | 'custom';

interface PlatformOption {
  label: string;
  commission: number;
  isBooking: boolean;
}

const PLATFORMS: Record<Exclude<PlatformKey, 'custom'>, PlatformOption> = {
  'booking-standard': { label: 'Booking.com - Standard', commission: 16.3, isBooking: true },
  'booking-preferred': { label: 'Booking.com - Preferente', commission: 18.3, isBooking: true },
  airbnb: { label: 'Airbnb', commission: 15.5, isBooking: false },
};

// --- Helpers ---
function calcMarkup(commissionPct: number, genius: boolean): number {
  const c = commissionPct / 100;
  return genius ? 1 / (0.9 * (1 - c)) - 1 : c / (1 - c);
}

// --- Component ---
// El azul de Booking (#003580) desaparece sobre el fondo oscuro; en modo
// oscuro se aclara ese tono y los grises del gráfico, el resto aguanta.
const CHART_LIGHT = {
  bookingStd: '#003580',
  bookingStdGenius: '#0066cc',
  grid: '#e2e8f0',
  axis: '#64748b',
  tooltip: { borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 },
};
const CHART_DARK = {
  bookingStd: '#5b9bf0',
  bookingStdGenius: '#93c5fd',
  grid: '#374151',
  axis: '#9ca3af',
  tooltip: { borderRadius: 8, border: '1px solid #4b5563', fontSize: 13, backgroundColor: '#1f2937', color: '#f3f4f6' },
};

export default function CalculadoraOTAPage() {
  const chart = useIsDark() ? CHART_DARK : CHART_LIGHT;
  const [platform, setPlatform] = useState<PlatformKey>('booking-standard');
  const [customCommission, setCustomCommission] = useState(15);
  const [basePrice, setBasePrice] = useState(100);
  const [genius, setGenius] = useState(false);

  const isBooking = platform === 'booking-standard' || platform === 'booking-preferred';
  const commission =
    platform === 'custom' ? customCommission : PLATFORMS[platform].commission;
  const geniusActive = genius && isBooking;

  const result = useMemo(() => {
    const markup = calcMarkup(commission, geniusActive);
    const markupPct = markup * 100;
    const otaPrice = basePrice * (1 + markup);
    const finalPrice = geniusActive ? otaPrice * 0.9 : otaPrice;
    const commissionAmt = finalPrice * (commission / 100);
    const netEarnings = finalPrice - commissionAmt;

    return { markup, markupPct, otaPrice, finalPrice, commissionAmt, netEarnings };
  }, [commission, basePrice, geniusActive]);

  // Platform comparison cards
  const platformCards = useMemo(() => {
    const cards = [
      {
        key: 'booking-standard',
        name: 'Booking Standard',
        commission: 16.3,
        color: chart.bookingStd,
        markup: calcMarkup(16.3, false),
        geniusMarkup: genius ? calcMarkup(16.3, true) : null,
      },
      {
        key: 'booking-preferred',
        name: 'Booking Preferente',
        commission: 18.3,
        color: '#feba02',
        markup: calcMarkup(18.3, false),
        geniusMarkup: genius ? calcMarkup(18.3, true) : null,
      },
      {
        key: 'airbnb',
        name: 'Airbnb',
        commission: 15.5,
        color: '#ff5a5f',
        markup: calcMarkup(15.5, false),
        geniusMarkup: null,
      },
    ];
    if (platform === 'custom' && customCommission > 0) {
      cards.push({
        key: 'custom',
        name: 'Personalizado',
        commission: customCommission,
        color: '#10b981',
        markup: calcMarkup(customCommission, false),
        geniusMarkup: null,
      });
    }
    return cards;
  }, [platform, customCommission, genius, chart]);

  // Chart data: markup range 10% to 45%
  const chartData = useMemo(() => {
    const data = [];
    for (let m = 10; m <= 45; m += 1) {
      const mkup = m / 100;
      const otaP = basePrice * (1 + mkup);
      const gP = otaP * 0.9;
      const point: Record<string, number> = {
        markup: m,
        directa: basePrice,
        bookingStd: +(otaP * (1 - 0.163)).toFixed(2),
        bookingPref: +(otaP * (1 - 0.183)).toFixed(2),
        airbnb: +(otaP * (1 - 0.155)).toFixed(2),
      };
      if (genius) {
        point.bookingStdGenius = +(gP * (1 - 0.163)).toFixed(2);
        point.bookingPrefGenius = +(gP * (1 - 0.183)).toFixed(2);
      }
      if (platform === 'custom' && customCommission > 0) {
        point.custom = +(otaP * (1 - customCommission / 100)).toFixed(2);
      }
      data.push(point);
    }
    return data;
  }, [basePrice, genius, platform, customCommission]);

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div>
        <Link
          to="/calculadoras"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-sereno-dark transition-colors mb-3"
        >
          <ArrowLeft size={16} /> Calculadoras
        </Link>
        <h1 className="text-2xl font-bold text-sereno-dark">
          Calculadora de Comisiones OTA
        </h1>
        <p className="text-slate-500 mt-1">
          Calcula el precio óptimo para igualar ganancias entre venta directa y OTAs
        </p>
      </div>

      {/* Main grid: config + result */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Config card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
          <h3 className="font-semibold text-sereno-dark text-lg">Configuración</h3>

          {/* Platform */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              Plataforma OTA
            </label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as PlatformKey)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            >
              <option value="booking-standard">Booking.com - Standard (16.3%)</option>
              <option value="booking-preferred">Booking.com - Preferente (18.3%)</option>
              <option value="airbnb">Airbnb (15.5%)</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>

          {/* Custom commission */}
          {platform === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">
                Comisión personalizada (%)
              </label>
              <input
                type="number"
                min={0}
                max={50}
                step={0.1}
                value={customCommission}
                onChange={(e) => setCustomCommission(parseFloat(e.target.value) || 0)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              />
            </div>
          )}

          {/* Genius checkbox (only for Booking) */}
          {(isBooking || platform === 'custom') && isBooking && (
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={genius}
                onChange={(e) => setGenius(e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">
                  Descuento Genius (10%)
                </span>
                <p className="text-xs text-slate-400 mt-0.5">
                  Los clientes Genius reciben 10% de descuento sobre el precio final
                </p>
              </div>
            </label>
          )}

          {/* Base price */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              Precio base (venta directa) €
            </label>
            <input
              type="number"
              min={1}
              step={1}
              value={basePrice}
              onChange={(e) => setBasePrice(parseFloat(e.target.value) || 100)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Result card */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-5">
          <h3 className="font-semibold text-sereno-dark text-lg">Resultado</h3>

          {/* Markup badge */}
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl p-6 text-center border border-blue-100">
            <p className="text-sm text-slate-500 mb-1">Markup necesario</p>
            <p className="text-4xl font-bold text-sereno-dark">
              {result.markupPct.toFixed(2)}%
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Fórmula: Comisión / (1 − Comisión)
              {geniusActive && ' × factor Genius'}
            </p>
          </div>

          {/* Breakdown */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2.5 text-sm">
            <h4 className="font-medium text-slate-700 mb-2">
              Ejemplo con tu precio:
            </h4>
            <Row label="Precio base (venta directa)" value={`${basePrice.toFixed(2)} €`} />
            <Row
              label="Precio en OTA (con markup)"
              value={
                geniusActive
                  ? `${result.otaPrice.toFixed(2)} € → ${result.finalPrice.toFixed(2)} € (Genius −10%)`
                  : `${result.otaPrice.toFixed(2)} €`
              }
            />
            <Row label="Comisión OTA" value={`−${result.commissionAmt.toFixed(2)} €`} className="text-red-600" />
            <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold text-sereno-dark">
              <span>Tu ganancia neta</span>
              <span>{result.netEarnings.toFixed(2)} €</span>
            </div>
          </div>
        </div>
      </div>

      {/* Platform comparison cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {platformCards.map((p) => (
          <div
            key={p.key}
            className="bg-white rounded-xl border border-slate-200 p-4 text-center shadow-sm"
            style={{ borderTopColor: p.color, borderTopWidth: 3 }}
          >
            <h4 className="text-sm font-semibold text-sereno-dark">{p.name}</h4>
            <p className="text-xs text-slate-400 mt-0.5">Comisión: {p.commission}%</p>
            <p className="text-2xl font-bold text-sereno-dark mt-2">
              +{(p.markup * 100).toFixed(2)}%
            </p>
            {p.geniusMarkup !== null && (
              <p className="text-xs text-amber-600 font-medium mt-1">
                Con Genius: +{(p.geniusMarkup * 100).toFixed(2)}%
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="font-semibold text-sereno-dark text-lg mb-1">
          Comparativa de Ganancias según Markup
        </h3>
        <p className="text-xs text-slate-400 mb-5">
          La línea punteada muestra tu ganancia en venta directa. Donde cada línea la cruza es el markup óptimo.
        </p>
        <div className="h-80 sm:h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis
                dataKey="markup"
                tickFormatter={(v: number) => `${v}%`}
                tick={{ fill: chart.axis, fontSize: 12 }}
                label={{ value: 'Markup aplicado', position: 'insideBottom', offset: -2, fill: chart.axis, fontSize: 12 }}
              />
              <YAxis
                tickFormatter={(v: number) => `${v}€`}
                tick={{ fill: chart.axis, fontSize: 12 }}
                label={{ value: 'Ganancia neta (€)', angle: -90, position: 'insideLeft', fill: chart.axis, fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: unknown, name: unknown) => [`${Number(value).toFixed(2)} €`, String(name)]}
                labelFormatter={(v: unknown) => `Markup: ${v}%`}
                contentStyle={chart.tooltip}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />

              <Line
                dataKey="directa"
                name="Venta Directa"
                stroke="#94a3b8"
                strokeWidth={2}
                strokeDasharray="8 4"
                dot={false}
              />

              {!genius && (
                <>
                  <Line dataKey="bookingStd" name="Booking Standard (16.3%)" stroke={chart.bookingStd} strokeWidth={2} dot={false} />
                  <Line dataKey="bookingPref" name="Booking Preferente (18.3%)" stroke="#feba02" strokeWidth={2} dot={false} />
                </>
              )}
              <Line dataKey="airbnb" name="Airbnb (15.5%)" stroke="#ff5a5f" strokeWidth={2} dot={false} />

              {genius && (
                <>
                  <Line dataKey="bookingStdGenius" name="Booking Std + Genius" stroke={chart.bookingStdGenius} strokeWidth={2} dot={false} />
                  <Line dataKey="bookingPrefGenius" name="Booking Pref + Genius" stroke="#ff9900" strokeWidth={2} dot={false} />
                </>
              )}

              {platform === 'custom' && customCommission > 0 && (
                <Line dataKey="custom" name={`Personalizado (${customCommission}%)`} stroke="#10b981" strokeWidth={2} dot={false} />
              )}

              <ReferenceLine y={basePrice} stroke="#94a3b8" strokeDasharray="8 4" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Info section */}
      <div className="bg-blue-50 rounded-xl border border-blue-100 p-5 space-y-3 text-sm text-slate-600">
        <div className="flex items-center gap-2 text-sereno-dark font-semibold">
          <Info size={18} /> ¿Cómo funciona?
        </div>
        <p>
          Cuando vendes a través de una OTA, la plataforma cobra una comisión sobre el precio de venta.
          Para que tu ganancia neta sea la misma que vendiendo directamente, necesitas aplicar un <strong>markup</strong>.
        </p>
        <p>
          <strong>Fórmula:</strong>{' '}
          <code className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs">
            Markup = Comisión / (1 − Comisión)
          </code>
        </p>
        <p>
          Ejemplo: Si Booking cobra 16.3%, el markup es{' '}
          <code className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs">
            0.163 / (1 − 0.163) = 19.47%
          </code>.
          Precio directo 100€ → en Booking 119.47€ → tras comisión (19.47€) recibes 100€.
        </p>
      </div>
    </div>
  );
}

// --- Small components ---
function Row({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={className || 'text-slate-700'}>{value}</span>
    </div>
  );
}
